/**
 * Annotation React Query Hooks (shared)
 *
 * Create and list annotations. Cache invalidation on create
 * refreshes both shared and Sales-specific annotation queries
 * (they share the ['annotations'] key prefix).
 */

import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { annotationApi } from '../api/annotationApi';
import type { Annotation, CreateNoteInput } from './types';

export const annotationKeys = {
  all: ['annotations'] as const,
  forRecord: (objectId: string) => [...annotationKeys.all, 'record', objectId] as const,
};

export function useAnnotationsForRecord(
  objectId: string,
  options?: Omit<UseQueryOptions<Annotation[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: annotationKeys.forRecord(objectId),
    queryFn: () => annotationApi.listForRecord(objectId),
    enabled: Boolean(objectId),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNoteInput) => annotationApi.createNote(input),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: annotationKeys.forRecord(variables.accountId) });
      qc.invalidateQueries({ queryKey: annotationKeys.all });
    },
  });
}
